/**
 * Turning an assistant HTTP refusal into words for the chat panel (#859).
 *
 * The chat routes answer 429 when a per-user or per-tenant limit trips, with a JSON
 * body the server words itself (`{ error: 'rate_limited', scope, retryAfterSeconds,
 * message }`) and a standard `Retry-After` header. Prefer the server's message; fall
 * back to the header, then to a plain sentence, so a proxy that strips the body
 * still leaves the user something true to read.
 */

/** Thrown by the widget when the server refused the question for a reason the user can act on. */
export class AssistantRefusedError extends Error {
  constructor(readonly userMessage: string) {
    super(userMessage);
    this.name = 'AssistantRefusedError';
  }
}

/** The server's own `message`, if the body is JSON and carries a non-empty one. */
async function serverMessage(res: Response): Promise<string | null> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string') {
      const message = (body as { message: string }).message.trim();
      if (message) return message;
    }
  } catch {
    // Not JSON (e.g. a proxy's HTML error page).
  }
  return null;
}

/**
 * 403 from the chat routes (#860): the tenant's assistant switch is off. A page
 * loaded before the switch changed can still show the panel; say why it stopped.
 */
export async function notEnabledMessage(res: Response): Promise<string> {
  return (await serverMessage(res)) ?? "The assistant isn't enabled for your organisation.";
}

export async function rateLimitedMessage(res: Response): Promise<string> {
  const message = await serverMessage(res);
  if (message) return message;
  const retryAfter = Number(res.headers.get('Retry-After'));
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return `You're asking faster than the assistant allows. Try again in ${Math.ceil(retryAfter)} s.`;
  }
  return "You're asking faster than the assistant allows. Try again in a moment.";
}
